import React, { useState, useEffect } from "react";
import { X, Search } from "lucide-react";
import Button from "components/MaterialButton";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";

const CreateWarehouseMaterial = ({ onClose, onSuccess, onRedirectToMaterialLibrary }) => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [currentQuantity, setCurrentQuantity] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { addAlert } = useAlert();
  const [organisationId, setOrganisationId] = useState(
    JSON.parse(localStorage.getItem("organization"))?.id
  );

  useEffect(() => {
    getMaterials();
  }, []);

  const getMaterials = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/org/material-library/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ org_id: organisationId }),
      });

      if (!res.ok) {
        throw new Error("Failed to fetch materials");
      }

      const data = await res.json();
      setMaterials(data);
    } catch (err) {
      console.error("Error fetching materials:", err);
    } finally {
      setLoading(false);
    }
  };

  const createWarehouseMaterial = async () => {
    if (!selectedMaterial || !currentQuantity) {
      alert("Please select a material and enter quantity");
      return;
    }

    try {
      setSubmitting(true);

      const res = await fetch(`${BASE_URL}/org/warehouse/create/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
        body: JSON.stringify({
          organization: organisationId,
          material: selectedMaterial.id,
          current_quantity: currentQuantity,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        addAlert(
          errorData.error || "Failed to create warehouse material",
          "error"
        );
      }

      const data = await res.json();
      onSuccess?.(data);
      onClose();
    } catch (err) {
      // alert(err.message || "Failed to create warehouse material");
      console.error("Error creating warehouse material:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredMaterials = materials.filter(
    (material) =>
      material.material_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      material.subcategory?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.specification?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleMaterialSelect = (material) => {
    setSelectedMaterial(material);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[600px] max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-[#232323]">
            Add Material to Warehouse
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          {/* Search Materials */}
          <div>
          </div>

          {/* Material Selection */}
          <div>
            <p className="text-sm text-gray-600 mb-1">
              Can't find your material?{' '}
              <button
                type="button"
                onClick={() => {
                  onClose?.();
                  onRedirectToMaterialLibrary?.();
                }}
                className="text-[#3B0069] underline"
              >
                Click here
              </button>
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Material *
            </label>
            {loading ? (
              <div className="flex items-center justify-center py-8 border border-gray-300 rounded-md">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3B0069]"></div>
                <span className="ml-2 text-gray-600">Loading...</span>
              </div>
            ) : (
              <select
                value={selectedMaterial?.id || ""}
                onChange={(e) => {
                  const material = materials.find(
                    (m) => m.id === parseInt(e.target.value)
                  );
                  handleMaterialSelect(material);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#3B0069]"
              >
                <option value="">Select a material</option>
                {filteredMaterials.map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.material_name}
                    {material.subcategory && ` - ${material.subcategory}`}
                    {material.uom && ` (${material.uom})`}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Selected Material Details */}
          {selectedMaterial && (
            <div className="p-3 bg-gray-50 rounded-md border">
              <div className="text-sm text-gray-600">
                <p>
                  <span className="font-medium">Material:</span>{" "}
                  {selectedMaterial.material_name}
                </p>
                {selectedMaterial.specification && (
                  <p>
                    <span className="font-medium">Specification:</span>{" "}
                    {selectedMaterial.specification}
                  </p>
                )}
                {selectedMaterial.subcategory && (
                  <p>
                    <span className="font-medium">Category:</span>{" "}
                    {selectedMaterial.subcategory}
                  </p>
                )}
                {selectedMaterial.brand_name && (
                  <p>
                    <span className="font-medium">Brand:</span>{" "}
                    {selectedMaterial.brand_name}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Quantity Input */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Initial Quantity *
              </label>
              <input
                type="number"
                placeholder="Enter quantity"
                value={currentQuantity}
                onChange={(e) => setCurrentQuantity(e.target.value)}
                min="0"
                step="any"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#3B0069]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit
              </label>
              <input
                type="text"
                value={selectedMaterial?.uom || ""}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                placeholder="Select material first"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={createWarehouseMaterial}
              disabled={!selectedMaterial || !currentQuantity || submitting}
              className="px-6 py-2 bg-[#3B0069] text-white rounded-md hover:bg-[#2d0052] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Adding..." : "Add Material"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateWarehouseMaterial;
